import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { useMemo } from 'react';
import { ORGANIC_SOURCE_NAME } from '../../utils';

const getSelectedValues = dimension =>
    !dimension.selected.values ? [] : dimension.values.filter(v => dimension.selected.values.includes(v.name));

const groupSitesByApp = (selectedValues, allValues) => {
    const totalSitesPerApp = {};
    allValues.forEach(v => {
        const app = v.app || v;
        totalSitesPerApp[app.name] = (totalSitesPerApp[app.name] || 0) + 1;
    });

    const appGroups = {};
    selectedValues.forEach(v => {
        const app = v.app || v;
        const appKey = app.name;
        if (!appGroups[appKey]) {
            appGroups[appKey] = { ...app, sites: [], totalSites: totalSitesPerApp[appKey] || 0 };
        }
        appGroups[appKey].sites.push(v);
    });
    return sortBy(Object.values(appGroups), x => (x.displayName || '').toUpperCase());
};

export const getDimensionData = dimension => {
    const selectedValues = getSelectedValues(dimension);
    return {
        displayName: dimension.displayName,
        selectedCount: selectedValues.length,
        totalCount: dimension.values.length,
        items: selectedValues,
        addFuture: dimension.selected.addFuture,
    };
};

const useReviewData = ({ permissionDimensions, disabledSourceNames, screens, fields }) => {
    const isAppLevel = !!permissionDimensions.app;

    const appsData = useMemo(() => {
        const appDimension = permissionDimensions.app || permissionDimensions.app_site_id;
        if (!appDimension) return null;

        const selectedValues = getSelectedValues(appDimension);
        const displayItems = isAppLevel
            ? sortBy(selectedValues, x => (x.displayName || '').toUpperCase())
            : groupSitesByApp(selectedValues, appDimension.values);

        const totalCount = isAppLevel
            ? appDimension.values.length
            : uniqBy(
                  appDimension.values.map(v => v.app || v),
                  'name'
              ).length;

        return {
            items: displayItems,
            totalCount,
            addFuture: appDimension.selected.addFuture,
            selectedSitesCount: isAppLevel ? 0 : selectedValues.length,
            totalSitesCount: isAppLevel ? 0 : appDimension.values.length,
        };
    }, [permissionDimensions, isAppLevel]);

    const sourcesData = useMemo(() => {
        if (!permissionDimensions.base_source) return null;
        const selectedValues = getSelectedValues(permissionDimensions.base_source);
        const sortedValues = [
            ...selectedValues.filter(x => x.name === ORGANIC_SOURCE_NAME),
            ...sortBy(
                selectedValues.filter(x => x.name !== ORGANIC_SOURCE_NAME),
                x => (x.displayName || '').toUpperCase()
            ),
        ];
        const disabledSet = new Set(disabledSourceNames);
        return {
            items: sortedValues,
            totalCount: permissionDimensions.base_source.values.filter(v => !disabledSet.has(v.name)).length,
            addFuture: permissionDimensions.base_source.selected.addFuture,
        };
    }, [permissionDimensions, disabledSourceNames]);

    const screensData = useMemo(() => {
        if (!screens) return null;
        const { values, addFuture, overall } = screens;
        const selectedCount = values.reduce((total, section) => total + section.children.length, 0);
        return { values, addFuture, selectedCount, overall };
    }, [screens]);

    const fieldsData = useMemo(() => {
        if (!fields) return null;
        const { values, addFuture, overall } = fields;
        const selectedCount = values.reduce((total, fieldType) => total + fieldType.values.length, 0);
        return { values, addFuture, selectedCount, overall };
    }, [fields]);

    return { appsData, sourcesData, screensData, fieldsData, isAppLevel };
};

export default useReviewData;
